import {
  BaseUser,
  DashboardAssigneeFilterValue,
  DashboardCard,
  DashboardCardFilters,
  DashboardDateFilterValue,
  DashboardFilterKey,
  DashboardFilterValue,
  DashboardUserFilterValue,
  type PatchDashboardCardPreferencesBody,
} from "@models";
import { FilterList } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Divider,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { Chip as FilterChip } from "src/components/Chip/Chip";
import Button from "src/components/GenericButton/GenericButton";

const FILTER_LABELS: Record<DashboardFilterKey, string> = {
  "document-author": "Author",
  "document-approver": "Approver",
  "task-due-date": "Due date",
  "task-status": "Task status",
  "tasks-assignee": "Assignee",
  "document-status": "Document status",
  "document-comment-last-reply-author": "Last person to comment",
  "document-comment-last-reply-date": "Last reply date",
};

const TASK_STATUS_LABELS: Record<string, string> = {
  TODO: "To do",
  IN_PROGRESS: "In progress",
  DONE: "Done",
};

const DOCUMENT_STATUS_LABELS: Record<string, string> = {
  AVAILABLE: "Available",
  DRAFT: "Draft",
  UNAPPROVED: "Unapproved",
  LIVE: "Live",
  ARCHIVED: "Archived",
};

const DATE_PRESET_LABELS: Record<string, string> = {
  overdue: "Overdue",
  today: "Today",
  next_7_days: "Next 7 days",
  no_due_date: "No due date",
};

const FILTER_MENU_UNMOUNT_DELAY_MS = 100;
const UNASSIGNED_ASSIGNEE_OPTION_ID = "unassigned";

type DashboardFilterOption = {
  label: string;
  value: string;
};

type DashboardUserFilterOption = {
  id: string;
  label: string;
  isUnassigned?: boolean;
};

type DashboardCardFilterControlProps = {
  cardKey: DashboardCard["key"];
  title: string;
  filterKeys: DashboardFilterKey[];
  filters: DashboardCardFilters;
  users: BaseUser[];
  onFiltersChange: (
    cardKey: DashboardCard["key"],
    filters: PatchDashboardCardPreferencesBody["filters"]
  ) => void;
};

const getUserName = (user: BaseUser) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email;
};

const isUserFilterKey = (
  filterKey: DashboardFilterKey
): filterKey is
  | "document-author"
  | "document-approver"
  | "document-comment-last-reply-author" =>
  filterKey === "document-author" ||
  filterKey === "document-approver" ||
  filterKey === "document-comment-last-reply-author";

const isAssigneeFilterKey = (filterKey: DashboardFilterKey) =>
  filterKey === "tasks-assignee";

const isUserBackedFilterKey = (filterKey: DashboardFilterKey) =>
  isUserFilterKey(filterKey) || isAssigneeFilterKey(filterKey);

const isFilterObjectValue = (
  value: DashboardFilterValue | undefined
): value is Exclude<DashboardFilterValue, string[]> =>
  Boolean(value) && !Array.isArray(value) && typeof value === "object";

const hasUserIdsFilterValue = (
  value: DashboardFilterValue | undefined
): value is DashboardUserFilterValue | DashboardAssigneeFilterValue =>
  isFilterObjectValue(value) &&
  "userIds" in value &&
  Array.isArray(value.userIds);

const hasAssigneeFilterValue = (
  value: DashboardFilterValue | undefined
): value is DashboardAssigneeFilterValue =>
  hasUserIdsFilterValue(value) && "includeUnassigned" in value;

const hasDatePresetFilterValue = (
  value: DashboardFilterValue | undefined
): value is DashboardDateFilterValue & { preset: string } =>
  isFilterObjectValue(value) &&
  "preset" in value &&
  typeof value.preset === "string";

const getArrayFilterValue = (value: DashboardFilterValue | undefined) => {
  if (!Array.isArray(value)) {
    return "";
  }

  return value[0] ?? "";
};

const getDatePresetValue = (value: DashboardFilterValue | undefined) => {
  if (!hasDatePresetFilterValue(value)) {
    return "";
  }

  return value.preset;
};

const getSelectedChipFilterValue = (
  filterKey: DashboardFilterKey,
  value: DashboardFilterValue | undefined
): string => {
  if (filterKey === "task-status" || filterKey === "document-status") {
    return getArrayFilterValue(value);
  }

  return getDatePresetValue(value);
};

const getNextChipFilterValue = (
  filterKey: DashboardFilterKey,
  value: string
): DashboardFilterValue | null => {
  if (!value) {
    return null;
  }

  if (filterKey === "task-status" || filterKey === "document-status") {
    return [value];
  }

  return {
    preset: value as "overdue" | "today" | "next_7_days" | "no_due_date",
  };
};

const getUserSelectAriaLabel = (filterKey: DashboardFilterKey) => {
  if (filterKey === "document-author") {
    return "Select Authors";
  }

  if (filterKey === "document-approver") {
    return "Select Approvers";
  }

  if (filterKey === "document-comment-last-reply-author") {
    return "Select people who commented last";
  }

  return "Select Assignees";
};

const getUserFilterOptions = (
  filterKey: DashboardFilterKey,
  users: BaseUser[]
): DashboardUserFilterOption[] => {
  const userOptions = users.map((user) => ({
    id: user.id,
    label: getUserName(user),
  }));

  if (!isAssigneeFilterKey(filterKey)) {
    return userOptions;
  }

  return [
    {
      id: UNASSIGNED_ASSIGNEE_OPTION_ID,
      label: "Unassigned",
      isUnassigned: true,
    },
    ...userOptions,
  ];
};

const getSelectedUserFilterOptions = (
  filterKey: DashboardFilterKey,
  value: DashboardFilterValue | undefined,
  users: BaseUser[]
) => {
  if (!hasUserIdsFilterValue(value)) {
    return [];
  }

  const selectedUserIds = new Set(value.userIds);
  const includeUnassigned =
    isAssigneeFilterKey(filterKey) &&
    hasAssigneeFilterValue(value) &&
    value.includeUnassigned === true;

  return getUserFilterOptions(filterKey, users).filter((option) =>
    option.isUnassigned ? includeUnassigned : selectedUserIds.has(option.id)
  );
};

const getNextUserFilterValue = (
  filterKey: DashboardFilterKey,
  selectedOptions: DashboardUserFilterOption[]
): DashboardFilterValue | null => {
  const userIds = selectedOptions
    .filter((option) => !option.isUnassigned)
    .map((option) => option.id);

  if (!isAssigneeFilterKey(filterKey)) {
    return userIds.length > 0 ? { userIds } : null;
  }

  const includeUnassigned = selectedOptions.some(
    (option) => option.isUnassigned
  );

  if (userIds.length === 0 && !includeUnassigned) {
    return null;
  }

  return includeUnassigned ? { userIds, includeUnassigned: true } : { userIds };
};

const getFilterOptions = (
  filterKey: DashboardFilterKey
): DashboardFilterOption[] => {
  if (isUserBackedFilterKey(filterKey)) {
    return [];
  }

  if (filterKey === "task-status") {
    return Object.entries(TASK_STATUS_LABELS).map(([value, label]) => ({
      label,
      value,
    }));
  }

  if (filterKey === "document-status") {
    return Object.entries(DOCUMENT_STATUS_LABELS).map(([value, label]) => ({
      label,
      value,
    }));
  }

  return Object.entries(DATE_PRESET_LABELS).map(([value, label]) => ({
    label,
    value,
  }));
};

const getFilterOptionTestId = (
  filterKey: DashboardFilterKey,
  optionValue: string
) => `dashboard-card-filter-option-${filterKey}-${optionValue}`;

const isFilterValueSelected = (
  filterKey: DashboardFilterKey,
  value: DashboardFilterValue | undefined
) => {
  if (isUserBackedFilterKey(filterKey)) {
    return (
      hasUserIdsFilterValue(value) &&
      (value.userIds.length > 0 ||
        (hasAssigneeFilterValue(value) && value.includeUnassigned === true))
    );
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return hasDatePresetFilterValue(value);
};

export const DashboardCardFilterControl = ({
  cardKey,
  title,
  filterKeys,
  filters,
  users,
  onFiltersChange,
}: DashboardCardFilterControlProps) => {
  const filterMenuId = useId();
  const filterButtonId = `${filterMenuId}-button`;
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [isFilterMenuMounted, setIsFilterMenuMounted] = useState(false);
  const [isFilterMenuVisible, setIsFilterMenuVisible] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const filterMenuUnmountTimerRef = useRef<number | null>(null);
  const isFilterMenuOpen = Boolean(filterAnchorEl);
  const activeFilterKeys = filterKeys.filter((filterKey) =>
    isFilterValueSelected(filterKey, filters[filterKey])
  );
  const hasActiveFilters = activeFilterKeys.length > 0;

  const cancelFilterMenuUnmount = useCallback(() => {
    if (filterMenuUnmountTimerRef.current === null) {
      return;
    }

    window.clearTimeout(filterMenuUnmountTimerRef.current);
    filterMenuUnmountTimerRef.current = null;
  }, []);

  const scheduleFilterMenuUnmount = useCallback(() => {
    cancelFilterMenuUnmount();

    filterMenuUnmountTimerRef.current = window.setTimeout(() => {
      filterMenuUnmountTimerRef.current = null;
      setIsFilterMenuMounted(false);
    }, FILTER_MENU_UNMOUNT_DELAY_MS);
  }, [cancelFilterMenuUnmount]);

  const handleCloseFilterMenu = useCallback(
    (shouldRestoreFocus = false) => {
      setFilterAnchorEl(null);
      scheduleFilterMenuUnmount();

      if (shouldRestoreFocus) {
        filterButtonRef.current?.focus();
      }
    },
    [scheduleFilterMenuUnmount]
  );

  const handleToggleFilterMenu = (event: MouseEvent<HTMLButtonElement>) => {
    if (isFilterMenuOpen) {
      handleCloseFilterMenu();
      return;
    }

    cancelFilterMenuUnmount();
    setIsFilterMenuMounted(true);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterOptionClick = (
    filterKey: DashboardFilterKey,
    optionValue: string
  ) => {
    const selectedValue = getSelectedChipFilterValue(
      filterKey,
      filters[filterKey]
    );

    onFiltersChange(cardKey, {
      [filterKey]:
        selectedValue === optionValue
          ? null
          : getNextChipFilterValue(filterKey, optionValue),
    });
  };

  const handleUserFilterChange = (
    filterKey: DashboardFilterKey,
    selectedOptions: DashboardUserFilterOption[]
  ) => {
    onFiltersChange(cardKey, {
      [filterKey]: getNextUserFilterValue(filterKey, selectedOptions),
    });
  };

  const handleResetFilters = () => {
    if (activeFilterKeys.length === 0) {
      return;
    }

    onFiltersChange(
      cardKey,
      activeFilterKeys.reduce<PatchDashboardCardPreferencesBody["filters"]>(
        (patch, filterKey) => ({
          ...patch,
          [filterKey]: null,
        }),
        {}
      )
    );
  };

  useEffect(() => {
    if (!filterAnchorEl) {
      setIsFilterMenuVisible(false);
      return;
    }

    setIsFilterMenuVisible(false);
    const frameId = window.requestAnimationFrame(() => {
      setIsFilterMenuVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [filterAnchorEl]);

  useEffect(() => {
    if (!isFilterMenuOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      handleCloseFilterMenu(true);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleCloseFilterMenu, isFilterMenuOpen]);

  useEffect(
    () => () => {
      if (filterMenuUnmountTimerRef.current !== null) {
        window.clearTimeout(filterMenuUnmountTimerRef.current);
      }
    },
    []
  );

  if (filterKeys.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        ref={filterButtonRef}
        aria-controls={isFilterMenuOpen ? filterMenuId : undefined}
        aria-expanded={isFilterMenuOpen ? "true" : undefined}
        aria-haspopup="menu"
        aria-label={`Filter ${title}`}
        id={filterButtonId}
        onClick={handleToggleFilterMenu}
        size="small"
        sx={{
          backgroundColor: hasActiveFilters
            ? "rgba(226, 232, 240, 0.7)"
            : "transparent",
          borderColor: hasActiveFilters
            ? "rgba(15, 23, 42, 0.2)"
            : "rgba(148, 163, 184, 0.5)",
          borderRadius: "0.875rem",
        }}
      >
        <FilterList fontSize="small" />
      </IconButton>
      {isFilterMenuMounted && (
        <>
          {isFilterMenuOpen && (
            <Box
              aria-label="Close dashboard card filters"
              onClick={() => handleCloseFilterMenu()}
              sx={{
                backgroundColor: "transparent",
                inset: 0,
                position: "fixed",
                zIndex: 1299,
              }}
            />
          )}
          <Popper
            data-testid="dashboard-card-filter-popper"
            anchorEl={filterAnchorEl}
            open={isFilterMenuOpen}
            keepMounted
            placement="right-start"
            modifiers={[
              {
                name: "offset",
                options: { offset: [0, 8] },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport",
                  padding: 8,
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: [
                    "top-end",
                    "bottom-end",
                    "left-start",
                    "left-end",
                  ],
                },
              },
            ]}
            style={{ zIndex: 1300 }}
          >
            <Paper
              id={filterMenuId}
              role="menu"
              aria-labelledby={filterButtonId}
              sx={{
                border: "1px solid rgba(148, 163, 184, 0.2)",
                borderRadius: "6px",
                boxShadow: "0 18px 50px rgba(15, 23, 42, 0.18)",
                maxHeight: "70vh",
                overflow: "hidden",
                visibility: isFilterMenuVisible ? "visible" : "hidden",
              }}
            >
              <Box
                data-testid="dashboard-card-filter-content"
                className="my-3 flex max-h-[70vh] flex-col overflow-hidden"
                sx={{
                  minHeight: 0,
                  minWidth: "280px",
                  maxWidth: "280px",
                  width: "min(280px, calc(100vw - 2rem))",
                }}
              >
                <Box
                  data-testid="dashboard-card-filter-header"
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: "7px",
                    justifyContent: "space-between",
                    margin: "10px 14px",
                  }}
                >
                  <Typography fontWeight="bold">Filter by</Typography>
                  <Button
                    size="small"
                    variant="text"
                    text="Reset Filters"
                    onClick={handleResetFilters}
                    dataTestId="dashboard-card-reset-filters"
                  />
                </Box>

                <Divider />

                <Box
                  data-testid="dashboard-card-filter-body"
                  className="min-h-0 flex-1 overflow-y-auto"
                  sx={{ paddingBottom: "12px" }}
                >
                  {filterKeys.map((filterKey) => {
                    if (isUserBackedFilterKey(filterKey)) {
                      const userOptions = getUserFilterOptions(
                        filterKey,
                        users
                      );
                      const selectedUserOptions =
                        getSelectedUserFilterOptions(
                          filterKey,
                          filters[filterKey],
                          users
                        );

                      if (userOptions.length === 0) {
                        return null;
                      }

                      return (
                        <Box key={filterKey} sx={{ paddingBottom: "8px" }}>
                          <Box
                            sx={{
                              alignItems: "center",
                              display: "flex",
                              margin: "4px 14px 0",
                              padding: "6px 0",
                              width: "calc(100% - 28px)",
                            }}
                          >
                            <Typography className="pt-5">
                              {FILTER_LABELS[filterKey]}
                            </Typography>
                          </Box>

                          <Box
                            className="px-2 pb-2 pt-1"
                            sx={{ margin: "4px 14px" }}
                          >
                            <Autocomplete<
                              DashboardUserFilterOption,
                              true,
                              false,
                              false
                            >
                              multiple
                              disablePortal
                              filterSelectedOptions
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, selectedValue) =>
                                option.id === selectedValue.id
                              }
                              onChange={(_, newValue) =>
                                handleUserFilterChange(filterKey, newValue)
                              }
                              options={userOptions}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  hiddenLabel
                                  inputProps={{
                                    ...params.inputProps,
                                    "aria-label":
                                      getUserSelectAriaLabel(filterKey),
                                  }}
                                />
                              )}
                              size="small"
                              value={selectedUserOptions}
                            />
                          </Box>
                        </Box>
                      );
                    }

                    const selectedValue = getSelectedChipFilterValue(
                      filterKey,
                      filters[filterKey]
                    );
                    const options = getFilterOptions(filterKey);

                    if (options.length === 0) {
                      return null;
                    }

                    return (
                      <Box key={filterKey} sx={{ paddingBottom: "8px" }}>
                        <Box
                          sx={{
                            alignItems: "center",
                            display: "flex",
                            margin: "4px 14px 0",
                            padding: "6px 0",
                            width: "calc(100% - 28px)",
                          }}
                        >
                          <Typography className="pt-5">
                            {FILTER_LABELS[filterKey]}
                          </Typography>
                        </Box>

                        <Box
                          className="my-2 mx-3 flex flex-wrap gap-2 px-2 pb-2 pt-1"
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            margin: "0px 14px",
                          }}
                        >
                          {options.map((option) => {
                            const isSelected = selectedValue === option.value;

                            return (
                              <FilterChip
                                key={option.value}
                                label={option.label}
                                size="small"
                                color={isSelected ? "info" : "default"}
                                sx={{
                                  width: "fit-content",
                                  ...(isSelected
                                    ? {
                                        "&&": {
                                          backgroundColor: "#5d85ff2b",
                                          color: "inherit",
                                        },
                                        "&&:hover": {
                                          backgroundColor: "#5d85ff2b",
                                          color: "inherit",
                                        },
                                      }
                                    : {}),
                                }}
                                onClick={() =>
                                  handleFilterOptionClick(
                                    filterKey,
                                    option.value
                                  )
                                }
                                aria-pressed={isSelected}
                                data-testid={getFilterOptionTestId(
                                  filterKey,
                                  option.value
                                )}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Paper>
          </Popper>
        </>
      )}
    </>
  );
};
